/* import __COLOCATED_TEMPLATE__ from './classification-list.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { enqueueTask } from "ember-concurrency";
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { showModal } from 'sh-www/utils/modal-controls';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { CLASSIFICATION_SECTIONS } from 'sh-www/models/classification';

const CLASS_NAME = 'SH-c-admin-classification-list';

const ACTIONS = {
  UPDATE: 'Update',
  DELETE: 'Delete',
  ADD: 'Add',
}

class ClassificationList extends Component {
  @service('commands/classification-commands') classificationCommandService;
  @service('state-managers/application') applicationStateManager;
  @service ('error-tracker') errorTracker;

  @tracked activeNavItem = CLASSIFICATION_SECTIONS.SUBJECT;

  get className() {
    return CLASS_NAME;
  }

  get classificationsToShow() {
    return this.args.classificationItems[this.activeNavItem];
  }

  get description() {
    switch (this.activeNavItem) {
      case CLASSIFICATION_SECTIONS.SUBJECT:
        return 'The topics covered by a completed work';
      case CLASSIFICATION_SECTIONS.MEDIUM:
        return 'The channels that a completed work was created to appear in';
      case CLASSIFICATION_SECTIONS.INDUSTRY:
        return 'The format of the completed work';
    }
  }

  get addNewClassificationText() {
    return `Add new ${this.activeNavItem.toLocaleLowerCase()}`
  }

  get navItems() {
    const navItems = [];

    Object.keys(CLASSIFICATION_SECTIONS).forEach((section) => {
        navItems.push({
          title: CLASSIFICATION_SECTIONS[section],
          isActive: () => this.activeNavItem === CLASSIFICATION_SECTIONS[section],
        });
    });

    return navItems;
  }

  @enqueueTask
  *addClassificationTask(label, category, type) {
    try {
      const response = yield this.classificationCommandService.createClassification(label, category, type);
      this.applicationStateManager.addToast("Successfully created classification.");
      this.args.loadClassifications.perform();
      return response;
    } catch (error) {
      this.errorTracker.error('addClassificationTask - failed to create classification', this, error);
      handleErrorResponseWithModal(this, error);
    }
  }

  @enqueueTask
  *updateClassificationTask(classsificationId, label, type) {
    try {
      const response = yield this.classificationCommandService.updateClassification(classsificationId, label, type);
      this.applicationStateManager.addToast("Successfully updated classification.");
      this.args.loadClassifications.perform();
      return response;
    } catch (error) {
      this.errorTracker.error(`updateClassificationTask - failed to update classification for ${classsificationId}`, this, error);
      handleErrorResponseWithModal(this, error);
    }
  }

  @enqueueTask
  *deleteClassificationTask(classsificationId) {
    try {
      const response = yield this.classificationCommandService.deleteClassification(classsificationId);
      this.applicationStateManager.addToast("Successfully deleted classification.");
      this.args.loadClassifications.perform();
      return response;
    } catch (error) {
      this.errorTracker.error(`deleteClassificationTask - failed to delete classification for ${classsificationId}`, this, error);
      this.applicationStateManager.addToast("Failed to delete classification. Please try again.");
    }
  }

  @action
  onNavItemClicked(tab) {
    this.activeNavItem = tab;
  }

  @action
  onAddClicked() {
    const category = this.activeNavItem.toLocaleLowerCase();

    const modalModel = {
      title: `Add new ${category}`,
      subTitle: `The new ${category} will become available to be added to completed works.`,
      actionType: ACTIONS.ADD,
      formLabel: `New ${category}:`,
      category: category,
      confirmAction: this.addClassificationTask,
    };

    showModal(this, 'admin/classification-list-modal', modalModel)
  }

  @action
  onEditClicked(classification) {
    const { category } = classification;

    const modalModel = {
      title: `Rename this ${category}`,
      subTitle: `The name of this ${category} will be updated on all completed works currently tagged with it.`,
      actionType: ACTIONS.UPDATE,
      formLabel: `Change ${category} name to:`,
      classificationId: classification.id,
      classificationType: classification.type,
      classificationLabel: classification.label,
      classificationCount: classification.count,
      confirmAction: this.updateClassificationTask,
    };

    showModal(this, 'admin/classification-list-modal', modalModel)
  }

  @action
  onDeleteClicked(classification) {
    const { category } = classification;

    const modalModel = {
      title: `Delete this ${category}`,
      subTitle: `This ${category} will be removed from all works currently tagged with it. You will no longer be able to add it to completed works.`,
      actionType: ACTIONS.DELETE,
      classificationId: classification.id,
      classificationLabel: classification.label,
      classificationCount: classification.count,
      confirmAction: this.deleteClassificationTask,
    };

    showModal(this, 'admin/classification-list-modal', modalModel)
  }
}

export default ClassificationList;
